import { useCallback, useMemo } from 'react';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { Option } from 'react-rainbow-components';
import Close from 'components/icons/close';
import getTimeOption from './helpers/getTimeOption';
import timeOptions from './helpers/timeOptions';
import {
    PickersContainer,
    RangePickerContainer,
    Separator,
    StyledPicklist,
    StyledButtonIcon,
} from './styled';

interface TimeRangePickerProps {
    from: number;
    to: number;
    min: number;
    max: number;
    onChange?: (value: Record<string, number>) => void;
    onDelete?: () => void;
}

const TimeRangePicker = ({
    from,
    to,
    min,
    max,
    onChange = () => {},
    onDelete = () => {},
}: TimeRangePickerProps) => {
    const fromPicklistValue = useMemo(() => getTimeOption(from), [from]);
    const toPicklistValue = useMemo(() => getTimeOption(to), [to]);

    const fromOptions = useMemo(
        () => {
            const options = timeOptions
                .slice(0, -1)
                .filter((option) => option.value >= min && option.value < max);
            if (
                fromPicklistValue
                && !options.some((option) => option.value === fromPicklistValue.value)
                && fromPicklistValue.value >= min
                && fromPicklistValue.value < max
            ) {
                options.push(fromPicklistValue);
                options.sort((left, right) => left.value - right.value);
            }
            return options.map((option) => (
                <Option key={`from-time_${option.name}`} {...option} />
            ));
        },
        [fromPicklistValue, min, max],
    );

    const toOptions = useMemo(
        () => {
            const options = timeOptions
                .filter((option) => option.value > from && option.value <= max);
            if (
                toPicklistValue
                && !options.some((option) => option.value === toPicklistValue.value)
                && toPicklistValue.value > from
                && toPicklistValue.value <= max
            ) {
                options.push(toPicklistValue);
                options.sort((left, right) => left.value - right.value);
            }
            return options.map((option) => (
                <Option key={`to-time_${option.name}`} {...option} />
            ));
        },
        [from, max, toPicklistValue],
    );

    const handleFromChange = useCallback((newFrom: PicklistValue) => {
        if (newFrom.value < to) {
            onChange({
                from: newFrom.value,
                to,
            });
        }
    }, [onChange, to]);

    const handleToChange = useCallback((newTo: PicklistValue) => {
        onChange({
            from,
            to: newTo.value,
        });
    }, [from, onChange]);

    return (
        <RangePickerContainer>
            <PickersContainer>
                <StyledPicklist
                    borderRadius="semi-rounded"
                    variant="bare"
                    value={fromPicklistValue}
                    onChange={handleFromChange}
                >
                    {fromOptions}
                </StyledPicklist>
                <Separator>-</Separator>
                <StyledPicklist
                    borderRadius="semi-rounded"
                    variant="bare"
                    value={toPicklistValue}
                    onChange={handleToChange}
                >
                    {toOptions}
                </StyledPicklist>
            </PickersContainer>
            <StyledButtonIcon
                size="small"
                borderRadius="semi-rounded"
                icon={<Close />}
                onClick={onDelete}
            />
        </RangePickerContainer>
    );
};

export default TimeRangePicker;
